import swal from 'sweetalert2';
import moment from 'moment-timezone';
import mixins from 'utils/client-mixins';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';

import config from 'config';
import router from 'router';
import campaignsApi from '../_campaigns-api';
import campaignsEnum from '../_campaigns-enum';
import { privileges } from 'domains/base/_base-enum';

export const getCampaigns = async ({ commit, dispatch, state, rootState }, { type, lazy }) => {
  let campaignsCollection = state.campaigns[type];

  if (!campaignsCollection.initialized || lazy) {
    let response = await dispatch('async', () => campaignsApi.getCampaigns(campaignsCollection));

    commit('setCampaigns', { type: campaignsCollection.type, response });

    return response;
  }
};

export const getStoreCampaigns = async ({ rootState, state, dispatch, commit }, lazy) => {
  let campaignsCollection = state.campaigns.store;

  if (!campaignsCollection.initialized || lazy) {
    let response = await dispatch('async', () => campaignsApi.getCampaigns(campaignsCollection, get(rootState.stores, 'currentStore.domain_id')));

    commit('setCampaigns', { type: 'store', response });

    return response;
  }
};

export const sortOrFilterCampaigns = ({ commit, dispatch }, { type, property, value }) => {
  commit('updateCampaignsCollection', { type, property, value });

  if (type === 'all') {
    return Promise.all([
      dispatch('getCampaignsChartsData', true),
      dispatch('getCampaigns', { type, lazy: false }),
    ]);
  } else {
    return dispatch('getCampaigns', { type, lazy: false });
  }
};

export const searchCampaigns = ({ rootState, dispatch, commit }, { type, value }) => {
  commit('updateCampaignsCollection', { type, property: 'searchQuery', value });

  if (type === 'store') {
    return dispatch('getStoreCampaigns', false);
  } else {
    return dispatch('getCampaigns', { type, lazy: false });
  }
};

const resetCampaigns = (dispatch, commit) => {
  commit('resetCampaigns', 'all');

  return Promise.all([
    dispatch('getCampaigns', { type: 'all' }),
    dispatch('getCampaignsChartsData'),
  ]);
};

const resetInfo = (campaignId, dispatch, commit) => {
  commit('setCurrentCampaign', { property: 'cache.info', value: false });

  return dispatch('getTabData', { campaignId, tab: campaignsEnum.INFO });
};

const setInProgressCampaignChanges = (original, currentCampaign, commit) => {
  let currentInfo = omit(currentCampaign.info, ['_links', 'end_date']),
    originalInfo = omit(original, ['_links', 'end_date']),
    areEqual = isEqual(currentInfo, originalInfo);

  if (!areEqual) {
    // because hateoas links changed based on business logic, we want the latest links
    originalInfo._links = currentCampaign.info._links;

    originalInfo.end_date = currentCampaign.info.end_date;

    commit('updateState', { object: currentCampaign, property: campaignsEnum.INFO, value: originalInfo });
  }
};

export const starCampaign = async ({ state, dispatch, commit }, campaignId) => {
  let campaignEdits = cloneDeep(state.currentCampaign.info),
    response = await dispatch('put', { apiUrl: campaignEdits._links.star.href });

  resetCampaigns(dispatch, commit);

  await resetInfo(campaignId, dispatch, commit);

  setInProgressCampaignChanges(campaignEdits, state.currentCampaign, commit);

  return response;
};

export const unstarCampaign = async ({ state, dispatch, commit }, campaignId) => {
  let campaignEdits = cloneDeep(state.currentCampaign.info),
    response = await dispatch('put', { apiUrl: campaignEdits._links.unstar.href });

  resetCampaigns(dispatch, commit);

  await resetInfo(campaignId, dispatch, commit);

  setInProgressCampaignChanges(campaignEdits, state.currentCampaign, commit);

  return response;
};

export const endCampaignEarly = async ({ state, dispatch, commit }, campaignId) => {
  let campaignEdits = cloneDeep(state.currentCampaign.info),
    response,
    currentRelaunchHasSales = await dispatch('checkIfCurrentRelaunchHasSales', campaignId);

  await swal({
    title: `End ${campaignEdits.name} Early?`,
    type: currentRelaunchHasSales ? 'question' : 'warning',
    showCancelButton: true,
    confirmButtonColor: config.ERROR_COLOR,
    confirmButtonText: 'End',
    showLoaderOnConfirm: true,
    html: currentRelaunchHasSales ? null : '<h5 class="vs-text-error"><strong>This campaign\'s latest relaunch currently has zero sales. Ending early will permanently end the campaign.</strong></h5>',
    preConfirm: async () => {
      try {
        response = await dispatch('put', { apiUrl: campaignEdits._links.end_early.href });

        commit('resetStores');

        resetCampaigns(dispatch, commit);

        await resetInfo(campaignId, dispatch, commit);

        setInProgressCampaignChanges(campaignEdits, state.currentCampaign, commit);

        return response;
      } catch (e) {
        return Promise.resolve();
      }
    },
  });

  return response;
};

export const extendCampaign = async ({ state, dispatch, commit }, campaignId) => {
  let campaignEdits = cloneDeep(state.currentCampaign.info),
    inputOptions = {
      '1': '1 Day',
      '2': '2 Days',
      '3': '3 Days',
      '4': '4 Days'
    },
    response;

  await swal({
    title: `Extend ${campaignEdits.name}?`,
    showCancelButton: true,
    confirmButtonColor: config.PRIMARY_COLOR,
    confirmButtonText: 'Extend',
    input: 'select',
    inputPlaceholder: 'Extension Length',
    inputOptions,
    inputValidator: (value) => {
      return new Promise(function(resolve, reject) {
        if (value) {
          resolve();
        } else {
          reject('Please Select an extension length!');
        }
      });
    },
    showLoaderOnConfirm: true,
    preConfirm: async extensionLength => {
      try {
        response = await dispatch('put', { apiUrl: campaignEdits._links.extend.href, payload: { num_days: extensionLength } });

        resetCampaigns(dispatch, commit);

        await resetInfo(campaignId, dispatch, commit);

        setInProgressCampaignChanges(campaignEdits, state.currentCampaign, commit);

        return response;
      } catch (e) {
        return Promise.resolve();
      }
    },
  });

  return response;
};

export const relaunchCampaign = async ({ state, dispatch, commit }, { campaignId, days, padSales }) => {
  let response = await dispatch('async', () => campaignsApi.relaunchCampaign(campaignId, days, padSales)),
    hateoasArray = response.campaign.split('/'),
    newCampaignId = hateoasArray[hateoasArray.length - 1];

  resetCampaigns(dispatch, commit);

  dispatch('getTabData', { campaignId: newCampaignId, tab: campaignsEnum.INFO });

  router.push({ name: 'campaignsDetailsInfo', params: { campaignId: newCampaignId } });

  return response;
};

export const deleteDraft = async ({ state, dispatch, commit }, campaign) => {
  let response;

  try {
    await swal({
      type: 'question',
      title: `Delete ${campaign.name} Draft?`,
      showCancelButton: true,
      confirmButtonColor: config.ERROR_COLOR,
      confirmButtonText: 'Delete',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          response = await dispatch('del', campaign._links.delete_draft.href);

          commit('resetCampaigns', 'draft');

          commit('updateState', { object: state.campaigns.draft, property: 'ignoredCampaignIds', value: [...state.campaigns.draft.ignoredCampaignIds, campaign.id] });

          dispatch('getCampaigns', { type: 'draft' });

          return response;
        } catch (e) {
          commit('addNotification', `An error occurred while attemping to delete draft ${campaign.name}`);

          commit('resetCampaigns', 'draft');

          dispatch('getCampaigns', { type: 'draft' });

          return Promise.resolve();
        }
      },
    });
  } catch (e) {}

  return response;
};

export const emailRep = async ({ commit, dispatch }, campaign) => {
  let response = await dispatch('get', campaign._links.cobrand_product_email);

  location.href = `mailto:?subject=${response.data.subject}&body=${response.data.body}`;

  commit('setEmailRepDetails', response.data);

  commit('lockScroll', true);

  return response;
};

export const getCampaignsChartsData = async ({ rootState, state, dispatch, commit }, force) => {
  if (
    (mixins.methods.hasPrivilege(privileges.DASHBOARD_VISITORS_CHART, rootState.currentUserRoleInfo.role) ||
      mixins.methods.hasPrivilege(privileges.DASHBOARD_VISITORS_DATA, rootState.currentUserRoleInfo.role)) &&
    (!state.campaignsAggregations.visitors.length || force)
  ) {
    let collection = state.campaigns.all,
      start = collection.start || moment().tz(rootState.currentUser.timezone).startOf('day'),
      end = collection.end || moment().tz(rootState.currentUser.timezone).endOf('day'),
      response = await dispatch('async', () => campaignsApi.getVisitors(start, end, null, collection));

    commit('setCampaignsChartsData', response.data);

    return response;
  }
};

export const getProductTypes = async ({ commit, dispatch, state }) => {
  let response = await dispatch('async', () => campaignsApi.getProductTypes());

  commit('updateState', { object: state, property: 'productTypes', value: response.data.product_types });

  return response;
};